import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { round } from 'lodash-es';

import { DualCoordinateDepthRange, LengthRange } from '@da-g3-acl/shared/common';

import { IntervalResizeHandle } from '../configurations';

import { IntervalDataContext } from './IntervalDataContext';
import { IntervalLaneRowContextProps, IntervalLaneRowProviderProps, IntervalsData } from './IntervalLaneRowProvider';
import { computeAllowableOverlapRanges } from './computeAllowableOverlapRanges';

const getAllowableRangeForInterval = (
	intervalRenderable: LengthRange,
	allRenderableRanges: LengthRange[],
	allowableRanges: LengthRange[] | undefined,
): LengthRange => {
	const above = [...allRenderableRanges]
		.sort((a, b) => b.depthTo - a.depthTo)
		.find((i) => i.depthTo < intervalRenderable.depthTo);
	const below = [...allRenderableRanges]
		.sort((a, b) => a.depthFrom - b.depthFrom)
		.find((i) => i.depthFrom > intervalRenderable.depthFrom);
	const inclusiveRanges = allowableRanges?.filter(
		(r) => r.depthFrom <= intervalRenderable.depthFrom && r.depthTo >= intervalRenderable.depthTo,
	);
	return {
		depthFrom: Math.max(
			...(inclusiveRanges?.map((r) => r.depthFrom) ?? []),
			...(above ? [above.depthTo] : []),
			-Infinity,
		),
		depthTo: Math.min(
			...(inclusiveRanges?.map((r) => r.depthTo) ?? []),
			...(below ? [below.depthFrom] : []),
			Infinity,
		),
	};
};

const getAllowableRangeForPoint = (
	point: number,
	allRenderableRanges: LengthRange[],
	allowableRanges: LengthRange[] | undefined,
): LengthRange => {
	const above = [...allRenderableRanges].sort((a, b) => b.depthTo - a.depthTo).find((i) => i.depthTo < point);
	const below = [...allRenderableRanges].sort((a, b) => a.depthFrom - b.depthFrom).find((i) => i.depthFrom > point);
	const inclusiveRanges = allowableRanges?.filter((r) => r.depthFrom <= point && r.depthTo >= point);
	return {
		depthFrom: Math.max(
			...(inclusiveRanges?.map((r) => r.depthFrom) ?? []),
			...(above ? [above.depthTo] : []),
			-Infinity,
		),
		depthTo: Math.min(
			...(inclusiveRanges?.map((r) => r.depthTo) ?? []),
			...(below ? [below.depthFrom] : []),
			Infinity,
		),
	};
};

const generateResizedRenderable = (
	original: LengthRange,
	handle: IntervalResizeHandle,
	resizePositionRaw: number,
	resizeBounds: LengthRange | undefined,
): LengthRange => {
	const resizePosition = round(resizePositionRaw, 3);
	const calcFrom =
		handle === IntervalResizeHandle.Start
			? resizePosition < original.depthTo
				? resizePosition
				: original.depthTo
			: handle === IntervalResizeHandle.End && resizePosition < original.depthFrom
				? resizePosition
				: original.depthFrom;
	const calcTo =
		handle === IntervalResizeHandle.End
			? resizePosition > original.depthFrom
				? resizePosition
				: original.depthFrom
			: handle === IntervalResizeHandle.Start && resizePosition > original.depthTo
				? resizePosition
				: original.depthTo;
	return {
		depthFrom: resizeBounds ? Math.max(resizeBounds.depthFrom, calcFrom) : calcFrom,
		depthTo: resizeBounds ? Math.min(resizeBounds.depthTo, calcTo) : calcTo,
	};
};

function generateDrawnRenderable(
	pointARaw: number,
	pointBRaw: number,
	allowableResizeRange: LengthRange | undefined,
): LengthRange {
	const pointA = round(pointARaw, 3);
	const pointB = round(pointBRaw, 3);
	return {
		depthFrom: Math.max(allowableResizeRange?.depthFrom ?? 0, Math.min(pointA, pointB)),
		depthTo: Math.min(allowableResizeRange?.depthTo ?? Infinity, Math.max(pointA, pointB)),
	};
}

// Geometry math operates in renderable space (what's painted). Ruler defaults to identity;
// consumers in uncropped mode override rulerDepth before persisting.
const asDualCoordinate = (renderable: LengthRange): DualCoordinateDepthRange => ({
	rulerDepth: renderable,
	renderableDepth: renderable,
});

export const useIntervalLaneRowData = (props: IntervalLaneRowProviderProps): IntervalLaneRowContextProps => {
	const { intervals, onIntervalCreating, onIntervalUpdating } = useContext(IntervalDataContext);
	const { mousePosition, allowableRanges, allowOverlapSetIds } = props;
	const [resizingInterval, setResizingInterval] = useState<{
		setId: string;
		interval: DualCoordinateDepthRange;
		handle: IntervalResizeHandle;
	}>();
	const [drawingInterval, setDrawingInterval] = useState<{
		setId: string;
		startDrawDepth: number;
		ctrlKey: boolean;
	}>();
	const [resizePosition, setResizePosition] = useState<number>();
	useEffect(() => {
		if (resizingInterval || drawingInterval) setResizePosition(mousePosition);
		else setResizePosition(undefined);
	}, [resizingInterval, mousePosition, drawingInterval]);

	const maxOverlapCount = drawingInterval
		? (props.maxOverlapCounts?.[drawingInterval.setId] ?? 0)
		: resizingInterval
			? (props.maxOverlapCounts?.[resizingInterval.setId] ?? 0)
			: 0;

	const allowableResizeRange = useMemo(() => {
		if (resizingInterval?.interval) {
			const otherRanges = intervals[resizingInterval.setId].intervals
				.filter((i) => !i.selected)
				.map((i) => i.renderableDepth);
			if (maxOverlapCount > 0) {
				const allowedRanges = computeAllowableOverlapRanges(otherRanges, maxOverlapCount, allowableRanges?.[0]);
				if (allowedRanges.length === 0) {
					return { ...resizingInterval.interval.renderableDepth };
				}
				const containingRange = allowedRanges.find(
					(r) =>
						r.depthFrom <= resizingInterval.interval.renderableDepth.depthFrom &&
						r.depthTo >= resizingInterval.interval.renderableDepth.depthTo,
				);
				return containingRange ?? allowedRanges[0];
			}
			return getAllowableRangeForInterval(
				resizingInterval.interval.renderableDepth,
				otherRanges,
				allowableRanges,
			);
		}
		if (drawingInterval) {
			const otherRanges = intervals[drawingInterval.setId].intervals
				.filter((i) => !i.selected)
				.map((i) => i.renderableDepth);
			if (maxOverlapCount > 0) {
				const allowedRanges = computeAllowableOverlapRanges(otherRanges, maxOverlapCount, allowableRanges?.[0]);
				if (allowedRanges.length === 0) {
					return {
						depthFrom: drawingInterval.startDrawDepth,
						depthTo: drawingInterval.startDrawDepth,
					};
				}
				const containingRange = allowedRanges.find(
					(r) => r.depthFrom <= drawingInterval.startDrawDepth && r.depthTo >= drawingInterval.startDrawDepth,
				);
				return containingRange ?? allowedRanges[0];
			}
			if (allowOverlapSetIds?.includes(drawingInterval.setId)) return undefined;
			return getAllowableRangeForPoint(drawingInterval.startDrawDepth, otherRanges, allowableRanges);
		}
		return undefined;
	}, [allowableRanges, intervals, resizingInterval, drawingInterval, allowOverlapSetIds, maxOverlapCount]);

	const editedIntervalsData: IntervalsData = useMemo(() => {
		let editIntervals = { ...intervals };
		if (resizingInterval) {
			editIntervals = {
				...intervals,
				[resizingInterval.setId]: {
					...intervals[resizingInterval.setId],
					intervals: intervals[resizingInterval.setId].intervals.map((i) => {
						if (
							i.renderableDepth.depthFrom === resizingInterval.interval.renderableDepth.depthFrom &&
							i.renderableDepth.depthTo === resizingInterval.interval.renderableDepth.depthTo &&
							resizePosition !== undefined
						) {
							const resized = generateResizedRenderable(
								i.renderableDepth,
								resizingInterval.handle,
								resizePosition,
								allowableResizeRange,
							);
							return { ...i, ...asDualCoordinate(resized) };
						}
						return i;
					}),
				},
			};
		}
		if (drawingInterval && resizePosition !== undefined) {
			const drawn = generateDrawnRenderable(drawingInterval.startDrawDepth, resizePosition, allowableResizeRange);
			editIntervals = {
				...intervals,
				[drawingInterval.setId]: {
					...intervals[drawingInterval.setId],
					intervals: [
						...intervals[drawingInterval.setId].intervals,
						{
							...asDualCoordinate(drawn),
							values: [{ colour: '#FFF' }],
							selected: true,
						},
					],
				},
			};
		}
		return editIntervals;
	}, [allowableResizeRange, intervals, resizePosition, resizingInterval, drawingInterval]);

	// Send onIntervalUpdating complete notification
	const handleIntervalResizeFinished = useCallback(() => {
		if (!resizingInterval || resizePosition === undefined) return;
		if (onIntervalUpdating) {
			const resized = generateResizedRenderable(
				resizingInterval.interval.renderableDepth,
				resizingInterval.handle,
				resizePosition,
				allowableResizeRange,
			);
			onIntervalUpdating(resizingInterval.setId, resizingInterval.interval, asDualCoordinate(resized), true);
		}
		setResizingInterval(undefined);
		setResizePosition(undefined);
	}, [allowableResizeRange, resizePosition, resizingInterval, onIntervalUpdating]);

	const handleIntervalDrawStarted = useCallback(
		(setId: string, clickDepth: number, ctrlKey: boolean) => {
			// If we clicked outside an allowable range, then do nothing
			if (
				allowableRanges
					? allowableRanges?.filter((r) => r.depthFrom <= clickDepth && r.depthTo >= clickDepth).length === 0
					: false
			)
				return;
			setDrawingInterval({ setId, startDrawDepth: clickDepth, ctrlKey });
			setResizePosition(clickDepth);
		},
		[allowableRanges],
	);

	// Send onIntervalCreating complete notification
	const handleIntervalDrawFinished = useCallback(() => {
		if (!drawingInterval || resizePosition === undefined) return;
		if (onIntervalCreating) {
			const drawn = generateDrawnRenderable(drawingInterval.startDrawDepth, resizePosition, allowableResizeRange);
			onIntervalCreating(drawingInterval.setId, asDualCoordinate(drawn), true, {
				ctrl: drawingInterval.ctrlKey,
			});
		}
		setDrawingInterval(undefined);
		setResizePosition(undefined);
	}, [allowableResizeRange, onIntervalCreating, drawingInterval, resizePosition]);

	// Send onIntervalCreating in-progress notification
	useEffect(() => {
		if (!drawingInterval || resizePosition === undefined || !onIntervalCreating) return;
		const drawn = generateDrawnRenderable(drawingInterval.startDrawDepth, resizePosition, allowableResizeRange);
		onIntervalCreating(drawingInterval.setId, asDualCoordinate(drawn), false, { ctrl: drawingInterval.ctrlKey });
	}, [drawingInterval, resizePosition, allowableResizeRange, onIntervalCreating]);

	// Send onIntervalUpdating in-progress notification
	useEffect(() => {
		if (!resizingInterval || resizePosition === undefined || !onIntervalUpdating) return;
		const resized = generateResizedRenderable(
			resizingInterval.interval.renderableDepth,
			resizingInterval.handle,
			resizePosition,
			allowableResizeRange,
		);
		onIntervalUpdating(resizingInterval.setId, resizingInterval.interval, asDualCoordinate(resized), false);
	}, [resizingInterval, resizePosition, allowableResizeRange, onIntervalUpdating]);

	return {
		handleIntervalResizeStarted: (setId, interval, handle): void => {
			setResizingInterval({ setId, interval, handle });
			setResizePosition(
				handle === 'start' ? interval.renderableDepth.depthFrom : interval.renderableDepth.depthTo,
			);
		},
		handleIntervalResizeFinished,
		handleIntervalDrawStarted,
		handleIntervalDrawFinished,
		intervalsData: editedIntervalsData,
		isEditingInterval: !!resizingInterval,
		isDrawingInterval: !!drawingInterval,
	};
};
